import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ChevronLeft, Crown, Check, Sparkles } from 'lucide-react';
import { useAuth } from '../App';
import AppleIAPButton from '../components/AppleIAPButton';
import { isNativePlatform } from '../lib/platform';

/**
 * Dedicated Subscription page — ALWAYS visible to all logged-in users.
 * On native (iOS/Android): displays Apple IAP options immediately at the top.
 * On web: redirects to /pricing (Square checkout).
 *
 * This page exists to comply with Apple Guideline 2.1(b) — IAP must be
 * easy to locate by App Review.
 */
const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const native = isNativePlatform();

  // On web, redirect to the existing pricing page
  React.useEffect(() => {
    if (!native) {
      navigate('/pricing', { replace: true });
    }
  }, [native, navigate]);

  if (!native) return null;

  const tier = user?.subscription_tier || 'free';
  const isActive = ['club', 'expert', 'founder'].includes(tier);

  const benefits = [
    'Calculateur de rentabilité complet (tous régimes fiscaux)',
    'Projets MDB illimités',
    'Dossier bancaire PDF',
    'Communauté complète + Badge Club',
    'Messages privés entre membres',
    'Biens immobiliers (publier & consulter)',
    'Formations exclusives',
    'Annuaire des membres',
    'Assistant IA (recherche, conseils)',
    'Statistiques IA & évolution des prix',
    'Alertes email automatiques',
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" data-testid="subscription-back-btn">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <h1 className="text-lg font-bold text-slate-900">Mon Abonnement</h1>
          <div className="w-16" />
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Header card */}
        <Card className="border-0 bg-gradient-to-br from-[#0D47A1] to-[#1565C0] text-white shadow-lg">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mx-auto mb-3">
              <Crown className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-1" data-testid="subscription-page-title">
              Club 10M Investor
            </h2>
            <p className="text-blue-100 text-sm">
              {isActive
                ? 'Vous êtes abonné. Merci de votre confiance !'
                : 'Débloquez toutes les fonctionnalités de la plateforme'}
            </p>
          </CardContent>
        </Card>

        {/* IAP block — VISIBLE FIRST, NO SCROLL NEEDED */}
        {!isActive && (
          <Card className="border-2 border-[#0D47A1] shadow-md" data-testid="subscription-iap-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-[#0D47A1]" />
                <h3 className="font-bold text-slate-900">Choisissez votre abonnement</h3>
              </div>
              <AppleIAPButton onPurchaseSuccess={() => window.location.reload()} />
            </CardContent>
          </Card>
        )}

        {isActive && (
          <Card className="border-green-200 bg-green-50" data-testid="subscription-active-card">
            <CardContent className="p-6 text-center">
              <Crown className="h-10 w-10 text-green-600 mx-auto mb-2" />
              <h3 className="font-bold text-slate-900 mb-1">Abonnement actif</h3>
              <p className="text-sm text-slate-600">
                Vous bénéficiez de l'ensemble des fonctionnalités du Club.
              </p>
              <p className="text-xs text-slate-500 mt-3">
                Pour gérer ou annuler votre abonnement, rendez-vous dans
                <strong> Réglages → Apple ID → Abonnements </strong>
                sur votre appareil iOS.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Benefits list */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-bold text-slate-900 mb-4">Ce qui est inclus</h3>
            <ul className="space-y-3">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="h-4 w-4 text-[#00B050] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Legal links */}
        <div className="text-center text-xs text-slate-500 space-y-1 py-4">
          <p>Abonnement auto-renouvelable. Annulable à tout moment.</p>
          <div className="flex justify-center gap-3">
            <a
              href="https://www.10minvestor.com/cgu"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-slate-700"
              data-testid="subscription-cgu-link"
            >
              Conditions d'utilisation
            </a>
            <span>|</span>
            <a
              href="https://www.10minvestor.com/confidentialite"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-slate-700"
              data-testid="subscription-privacy-link"
            >
              Politique de confidentialité
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
